import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Calendar, ChevronLeft, ChevronRight, RefreshCw,
  Truck, Check, Clock, AlertTriangle, Package, Star,
  Download, MapPin, User, Euro, Filter, ShoppingBag
} from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { formatDateWithDay } from '../utils/dateUtils';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const MONTH_NAMES = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
const DAY_NAMES = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

const EVENT_TYPE_CONFIG = {
  livraison:   { label: 'Livraison',    color: 'var(--admin-teal)', bg: 'rgba(93,188,191,0.12)',   icon: <Truck size={11} /> },
  reprise:     { label: 'Reprise',      color: 'var(--admin-blue)', bg: 'rgba(125,177,255,0.12)', icon: <Package size={11} /> },
  reservation: { label: 'Réservation',  color: 'var(--admin-green)', bg: 'rgba(31,157,122,0.12)', icon: <Check size={11} /> },
  en_attente:  { label: 'En attente',   color: 'var(--admin-amber)', bg: 'rgba(197,139,25,0.12)',  icon: <Clock size={11} /> },
  bloque:      { label: 'Bloqué',       color: 'var(--admin-red)', bg: 'rgba(229,62,62,0.12)',  icon: <AlertTriangle size={11} /> },
};

function EventPill({ event, onClick }) {
  const cfg = EVENT_TYPE_CONFIG[event.type] || EVENT_TYPE_CONFIG.en_attente;
  return (
    <button
      onClick={() => onClick(event)}
      className="w-full text-left px-1.5 py-0.5 rounded text-xs font-medium mb-0.5 flex items-center gap-1 truncate transition-opacity hover:opacity-80"
      style={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.color}25` }}
    >
      <span className="flex-shrink-0">{cfg.icon}</span>
      <span className="truncate">{event.titre}</span>
    </button>
  );
}

function EventDetail({ event, onClose }) {
  if (!event) return null;
  const cfg = EVENT_TYPE_CONFIG[event.type] || EVENT_TYPE_CONFIG.en_attente;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backdropFilter: 'blur(4px)', background: 'rgba(0,0,0,0.4)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-sm rounded-2xl p-6 space-y-4"
        style={{ background: 'var(--admin-bg)', border: '1px solid var(--admin-border)', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        {/* En-tête */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: cfg.bg }}>
              <span style={{ color: cfg.color }}>{cfg.icon}</span>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: cfg.color }}>{cfg.label}</p>
              <p className="font-bold text-sm" style={{ color: 'var(--admin-text)' }}>{event.titre}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg" style={{ color: 'var(--admin-muted)' }}>✕</button>
        </div>

        {/* Détails */}
        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2" style={{ color: 'var(--admin-muted)' }}>
            <Calendar size={14} />
            <span>{formatDateWithDay(event.date + 'T00:00:00')}</span>
            {event.heure && <span style={{ color: 'var(--admin-teal)', fontWeight: 600 }}>· {event.heure}</span>}
          </div>
          {event.adresse && (
            <div className="flex items-center gap-2" style={{ color: 'var(--admin-muted)' }}>
              <MapPin size={14} /><span>{event.adresse}</span>
            </div>
          )}
          {event.total !== undefined && event.total !== null && (
            <div className="flex items-center gap-2" style={{ color: 'var(--admin-muted)' }}>
              <Euro size={14} /><span className="font-bold price-gold">{Number(event.total).toFixed(2)}€</span>
            </div>
          )}
          {event.statut && (
            <div className="flex items-center gap-2">
              <Badge style={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.color}30` }}>
                {event.statut}
              </Badge>
            </div>
          )}
        </div>

        {/* Lien si devis */}
        {event.resource_id && event.type === 'reservation' && (
          <a href={`/devis/${event.resource_id}`} target="_blank" rel="noreferrer"
            className="flex items-center gap-2 text-sm font-semibold"
            style={{ color: 'var(--admin-teal)' }}>
            Voir le devis →
          </a>
        )}
      </div>
    </div>
  );
}

export default function AdminAgenda() {
  const now = new Date();
  const [mois, setMois] = useState(now.getMonth() + 1);
  const [annee, setAnnee] = useState(now.getFullYear());
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('calendrier');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [typeFilter, setTypeFilter] = useState('');

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND}/api/agenda/events?mois=${mois}&annee=${annee}`);
      const data = await res.json();
      setEvents(data.events || []);
    } catch (err) { if (process.env.NODE_ENV !== 'production') console.error(err); }
    setLoading(false);
  }, [mois, annee]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND}/api/agenda/stats`);
      const data = await res.json();
      setStats(data);
    } catch (err) { if (process.env.NODE_ENV !== 'production') console.error(err); }
  }, []);

  useEffect(() => {
    fetchEvents();
    fetchStats();
  }, [fetchEvents, fetchStats]);

  function prevMonth() {
    if (mois === 1) { setMois(12); setAnnee(a => a - 1); }
    else setMois(m => m - 1);
  }
  function nextMonth() {
    if (mois === 12) { setMois(1); setAnnee(a => a + 1); }
    else setMois(m => m + 1);
  }
  function goToday() {
    setMois(now.getMonth() + 1);
    setAnnee(now.getFullYear());
  }

  async function downloadICal() {
    try {
      const res = await fetch(`${BACKEND}/api/agenda/ical`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'show-evenement-agenda.ics'; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Erreur export iCal'); }
  }

  // Construction de la grille calendrier
  const firstDay = new Date(annee, mois - 1, 1);
  const lastDay = new Date(annee, mois, 0);
  const startDow = (firstDay.getDay() + 6) % 7; // Lundi = 0
  const totalDays = lastDay.getDate();

  // Jours du mois précédent pour remplir la grille
  const prevMonthDays = startDow;
  const totalCells = Math.ceil((prevMonthDays + totalDays) / 7) * 7;

  // Grouper les événements par date
  const filteredEvents = typeFilter ? events.filter(e => e.type === typeFilter) : events;
  const eventsByDate = {};
  filteredEvents.forEach(ev => {
    if (!eventsByDate[ev.date]) eventsByDate[ev.date] = [];
    eventsByDate[ev.date].push(ev);
  });

  // Liste triée des événements du mois
  const upcomingEvents = [...filteredEvents]
    .filter(e => e.date >= `${annee}-${String(mois).padStart(2,'0')}-01`)
    .sort((a, b) => a.date.localeCompare(b.date));

  const TABS = [
    { id: 'calendrier', label: 'Vue calendrier', icon: <Calendar size={15} /> },
    { id: 'liste', label: 'Vue liste', icon: <Filter size={15} /> },
  ];

  return (
    <div data-testid="admin-page">
      <div style={{ marginBottom: 20 }}>
        <p className="adm-eyebrow">Planning</p>
        <h2 style={{ fontFamily: 'var(--admin-font-display)', fontSize: 26, fontWeight: 700,
          letterSpacing: '-.6px', color: 'var(--admin-text)', marginTop: 4 }}>Agenda & Calendrier</h2>
        <p style={{ color: 'var(--admin-muted)', fontSize: 13.5, marginTop: 3 }}>Toutes vos dates et réservations</p>
      </div>


      <div data-testid="admin-agenda" style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      {/* Header */}
      <div className="flex items-center justify-end flex-wrap gap-3">
        <div className="flex gap-2">
          <Button onClick={() => { fetchEvents(); fetchStats(); }} variant="outline" size="sm">
            <RefreshCw size={14} className="mr-1" /> Actualiser
          </Button>
          <Button onClick={downloadICal} variant="outline" size="sm">
            <Download size={14} className="mr-1" /> Export iCal
          </Button>
        </div>
      </div>

      {/* Stats rapides */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: 'Ce mois-ci', value: events.length, color: 'var(--admin-teal)', icon: <Calendar size={16} /> },
            { label: 'Réservations', value: events.filter(e => e.type === 'reservation').length, color: 'var(--admin-green)', icon: <Check size={16} /> },
            { label: 'Livraisons', value: events.filter(e => e.type === 'livraison').length, color: 'var(--admin-teal)', icon: <Truck size={16} /> },
            { label: 'En attente', value: events.filter(e => e.type === 'en_attente').length, color: 'var(--admin-amber)', icon: <Clock size={16} /> },
          ].map((s, i) => (
            <div key={i} className="flex items-center gap-3 p-4 rounded-xl"
              style={{ background: '#fff', border: '1px solid var(--admin-border)' }}>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: `${s.color}15`, color: s.color }}>{s.icon}</div>
              <div>
                <p className="text-xs" style={{ color: 'var(--admin-muted)' }}>{s.label}</p>
                <p className="adm-title"  style={{ color: s.color }}>{s.value}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Navigation mois + Tabs */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <button onClick={prevMonth}
            className="w-9 h-9 rounded-xl flex items-center justify-center transition-colors hover:bg-gray-100"
            style={{ border: '1px solid var(--admin-border)', color: 'var(--admin-muted)' }}>
            <ChevronLeft size={18} />
          </button>
          <h2 className="font-bold text-lg min-w-[180px] text-center" style={{ color: 'var(--admin-text)' }}>
            {MONTH_NAMES[mois - 1]} {annee}
          </h2>
          <button onClick={nextMonth}
            className="w-9 h-9 rounded-xl flex items-center justify-center transition-colors hover:bg-gray-100"
            style={{ border: '1px solid var(--admin-border)', color: 'var(--admin-muted)' }}>
            <ChevronRight size={18} />
          </button>
          <Button onClick={goToday} variant="outline" size="sm" className="ml-2">Aujourd'hui</Button>
        </div>

        <div className="flex items-center gap-3">
          {/* Filtre type */}
          <select
            value={typeFilter}
            onChange={e => setTypeFilter(e.target.value)}
            className="px-3 py-2 rounded-xl text-sm"
            style={{ border: '1px solid var(--admin-border)', background: '#fff', color: 'var(--admin-text)' }}
          >
            <option value="">Tous les types</option>
            {Object.entries(EVENT_TYPE_CONFIG).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>

          {/* Tabs vue */}
          <div className="flex gap-1 p-1 rounded-xl" style={{ background: 'rgba(0,0,0,0.04)' }}>
            {TABS.map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
                style={activeTab === tab.id
                  ? { background: '#fff', color: 'var(--admin-teal)', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }
                  : { color: 'var(--admin-muted)' }}>
                {tab.icon}{tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Vue Calendrier ── */}
      {activeTab === 'calendrier' && (
        <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid var(--admin-border)', background: '#fff' }}>
          {/* En-têtes jours */}
          <div className="grid grid-cols-7" style={{ background: 'rgba(0,0,0,0.03)' }}>
            {DAY_NAMES.map(d => (
              <div key={d} className="p-3 text-center text-xs font-semibold uppercase tracking-wider"
                style={{ color: 'var(--admin-muted)' }}>{d}</div>
            ))}
          </div>

          {loading ? (
            <div className="p-8 text-center" style={{ color: 'var(--admin-muted)' }}>
              <div className="animate-spin w-8 h-8 rounded-full mx-auto" style={{ border: '2px solid var(--border)', borderTopColor: 'var(--teal)' }} />
            </div>
          ) : (
            <div className="grid grid-cols-7">
              {Array.from({ length: totalCells }, (_, i) => {
                const dayNum = i - prevMonthDays + 1;
                const isCurrentMonth = dayNum >= 1 && dayNum <= totalDays;
                const dateStr = isCurrentMonth
                  ? `${annee}-${String(mois).padStart(2,'0')}-${String(dayNum).padStart(2,'0')}`
                  : null;
                const dayEvents = dateStr ? (eventsByDate[dateStr] || []) : [];
                const todayStr = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
                const isToday = dateStr === todayStr;
                const isWeekend = (i % 7) >= 5;

                return (
                  <div key={i}
                    className="min-h-[100px] p-2"
                    style={{
                      borderTop: i >= 7 ? '1px solid var(--border)' : 'none',
                      borderLeft: (i % 7) !== 0 ? '1px solid var(--border)' : 'none',
                      background: !isCurrentMonth ? 'rgba(0,0,0,0.01)'
                        : isToday ? 'rgba(93,188,191,0.05)'
                        : isWeekend ? 'rgba(0,0,0,0.015)' : '#fff',
                    }}>
                    {/* Numéro du jour */}
                    <div className="flex items-center justify-between mb-1">
                      <span
                        className="text-xs font-semibold w-6 h-6 flex items-center justify-center rounded-full"
                        style={{
                          color: !isCurrentMonth ? 'var(--border)'
                            : isToday ? '#fff' : isWeekend ? '#C58B19' : 'var(--ink)',
                          background: isToday ? 'var(--teal)' : 'transparent',
                          fontWeight: isToday ? 700 : 600,
                        }}>
                        {isCurrentMonth ? dayNum : ''}
                      </span>
                      {dayEvents.length > 2 && (
                        <span className="text-xs" style={{ color: 'var(--admin-muted)' }}>+{dayEvents.length - 2}</span>
                      )}
                    </div>

                    {/* Événements du jour (max 2) */}
                    {dayEvents.slice(0, 2).map((ev, j) => (
                      <EventPill key={j} event={ev} onClick={setSelectedEvent} />
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Vue Liste ── */}
      {activeTab === 'liste' && (
        <div className="space-y-3">
          {loading ? (
            <div className="space-y-2">
              {[1,2,3,4,5].map(i => <Skeleton key={i} className="h-16 rounded-xl" />)}
            </div>
          ) : upcomingEvents.length === 0 ? (
            <div className="text-center py-16" style={{ color: 'var(--admin-muted)' }}>
              <Calendar size={40} className="mx-auto mb-3 opacity-30" />
              <p>Aucun événement ce mois-ci</p>
            </div>
          ) : (
            upcomingEvents.map((ev, i) => {
              const cfg = EVENT_TYPE_CONFIG[ev.type] || EVENT_TYPE_CONFIG.en_attente;
              const prevEv = upcomingEvents[i - 1];
              const showDateSep = !prevEv || prevEv.date !== ev.date;
              return (
                <React.Fragment key={ev.id}>
                  {showDateSep && (
                    <div className="flex items-center gap-3 pt-2">
                      <p className="text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--admin-muted)' }}>
                        {formatDateWithDay(ev.date + 'T00:00:00')}
                      </p>
                      <div className="flex-1 h-px" style={{ background: 'var(--border)' }} />
                    </div>
                  )}
                  <button
                    onClick={() => setSelectedEvent(ev)}
                    className="w-full flex items-center gap-4 p-4 rounded-xl text-left transition-all hover:shadow-sm"
                    style={{ background: '#fff', border: `1px solid ${cfg.color}25` }}
                  >
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background: cfg.bg, color: cfg.color }}>
                      {cfg.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm truncate" style={{ color: 'var(--admin-text)' }}>{ev.titre}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        {ev.heure && <span className="text-xs font-medium" style={{ color: cfg.color }}>{ev.heure}</span>}
                        {ev.adresse && (
                          <span className="text-xs flex items-center gap-1 truncate" style={{ color: 'var(--admin-muted)' }}>
                            <MapPin size={10} /> {ev.adresse}
                          </span>
                        )}
                      </div>
                    </div>
                    {ev.total !== undefined && ev.total !== null && (
                      <span className="font-bold text-sm price-gold flex-shrink-0">{Number(ev.total).toFixed(2)}€</span>
                    )}
                    <Badge style={{ background: cfg.bg, color: cfg.color, flexShrink: 0 }}>{cfg.label}</Badge>
                  </button>
                </React.Fragment>
              );
            })
          )}
        </div>
      )}

      {/* Légende */}
      <div className="flex flex-wrap gap-3">
        {Object.entries(EVENT_TYPE_CONFIG).map(([k, v]) => (
          <div key={k} className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-full" style={{ background: v.color }} />
            <span className="text-xs" style={{ color: 'var(--admin-muted)' }}>{v.label}</span>
          </div>
        ))}
      </div>

      {/* Modal détail */}
      {selectedEvent && <EventDetail event={selectedEvent} onClose={() => setSelectedEvent(null)} />}
      </div>
    </div>
  );
}
